/**
 * SMPP Frontend Utility Functions
 *
 * This module provides comprehensive utility functions for the SMPP frontend application.
 * It includes functions for data manipulation, file handling, user management,
 * UI interactions, and common operations used throughout the application.
 *
 * @author SMPP Development Team
 * @version 1.0.0
 * @since 2024
 */

import { XMLParser, XMLValidator, XMLBuilder } from "fast-xml-parser";
import { Document, File as DocxFile, Packer, Paragraph, TextRun } from "docx";
import { saveAs } from "file-saver";
import NProgress from "nprogress";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { ChartColors } from "../components/Report/Charts/ChartColors";
import {
  toastSuccessText, toastSuccessBg,
  toastErrorText, toastErrorBg,
  toastWarningText, toastWarningBg,
  toastInfoText, toastInfoBg,
  toastHeadingText, toastBodyText,
  paginationText,
  black,
} from "./colors";
import { dateFormatter2 } from "../Wrapper/CommonFun";
import { sizesArray } from "./utils";
import i18n from "../i18n/config";

// Configure NProgress with custom settings for better UX
NProgress.configure({ showSpinner: false, trickle: true });

/**
 * Handles modal closing when Escape key is pressed
 *
 * @param e - Keyboard event object
 * @param close - Function to close the modal
 * @returns void
 *
 * @example
 * ```typescript
 * <Modal onKeyDown={(e) => handleModalCloseOnEscape(e, closeModal)}>
 *   Modal content
 * </Modal>
 * ```
 */
export function handleModalCloseOnEscape(e, close: () => void): void {
  if (e.key === "Escape" && close) close();
}
/**
 * Removes an item from localStorage
 *
 * @param key - The key of the item to remove
 * @returns void
 */
export function removeItem(key: string) {
  localStorage.removeItem(key);
}

/**
 * Clears authentication-related data and resets the theme when a session ends.
 *
 * @returns void
 */
export function clearAuthStorage() {
  const appLanguage = localStorage.getItem("appLanguage");
  const locale = localStorage.getItem("i18nextLng");

  // Always wipe session-scoped data to prevent cross-user leakage.
  sessionStorage.clear();
  localStorage.clear();

  if (appLanguage) {
    localStorage.setItem("appLanguage", appLanguage);
  }
  if (locale) {
    localStorage.setItem("i18nextLng", locale);
  }

  document.documentElement.classList.remove("dark-mode", "dark");
  document.documentElement.style.colorScheme = "light";
  document.body?.classList.remove("dark-mode", "dark");
  if (document.body) document.body.style.colorScheme = "light";
  window.dispatchEvent(new Event("theme:reset"));
}

/**
 * Stores a value in localStorage with JSON serialization
 *
 * @param key - Storage key (must be a string)
 * @param value - Value to store (cannot be null or undefined)
 * @throws {Error} Logs error if key is not string or value is null/undefined
 *
 * @example
 * ```typescript
 * storeItem("userData", { id: 1, name: "John" });
 * storeItem("settings", { theme: "dark", language: "en" });
 * ```
 */
export function storeItem(key: string, value: any) {
  if (value === null || value === undefined || typeof key !== "string")
    console.error("key must be string and value must be provided");
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Retrieves an item from localStorage with optional JSON parsing
 *
 * @param key - Storage key to retrieve
 * @param jsonParse - Whether to parse the stored value as JSON
 * @returns Parsed object if jsonParse is true, raw string otherwise
 *
 * @example
 * ```typescript
 * const userData = getItem("userData", true); // Returns parsed object
 * const rawData = getItem("userData", false); // Returns raw string
 * ```
 */
export function getItem(key: string, jsonParse: boolean) {
  if (jsonParse) return JSON.parse(localStorage.getItem(key) ?? "false");
  return localStorage.getItem(key);
}
/**
 * Retrieves user data from localStorage in different formats based on the specified type
 *
 * @param id - Format type for the returned data:
 *   - "map": Returns the raw user IDs map object
 *   - "object": Returns array of objects with id and systemId properties
 *   - "systemId": Returns array of system IDs
 * @param length - Maximum number of items to return (only used for systemId format)
 * @returns User data in the specified format
 *
 * @example
 * ```typescript
 * const userMap = getLocalUser("map", 0);
 * const userObjects = getLocalUser("object", 0);
 * const systemIds = getLocalUser("systemId", 10); // Returns first 10 system IDs
 * ```
 */
export function getLocalUser(
  id: "map" | "object" | "systemId",
  length: number
): string[] | Record<number, string>[] {
  if (id === "map") return JSON.parse(getItem("userIdsMap", true));
  if (id === "object")
    return Object.entries(JSON.parse(getItem("userIdsMap", true))).map(
      ([id, systemId]) => ({ id, systemId })
    );
  return (
    Object[!id || id !== "systemId" ? "keys" : "values"](
      JSON.parse(getItem("userIdsMap", true))
    ).slice(0, length) ?? ""
  );
}
export const extractApiMessage = (
  payload: unknown,
  customMessage?: string
): string | undefined => {
  if (typeof customMessage === "string" && customMessage.trim()) {
    return customMessage;
  }

  if (!payload) return undefined;
  if (typeof payload === "string" && payload.trim()) return payload;
  if (typeof payload !== "object") return undefined;

  const record = payload as Record<string, unknown>;
  const nestedData =
    typeof record.data === "object" && record.data !== null
      ? (record.data as Record<string, unknown>)
      : undefined;

  const candidates = [
    record.message,
    record.errorMessage,
    nestedData?.message,
    typeof record.data === "string" ? record.data : undefined,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }

  return undefined;
};

type ToastTemplateMatcher = {
  regex: RegExp;
  englishTemplate: string;
  varNames: string[];
};

type ToastEnglishResolverCache = {
  exactMap: Map<string, string>;
  templateMatchers: ToastTemplateMatcher[];
};

let toastEnglishResolverCache: ToastEnglishResolverCache | null = null;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const flattenTranslationObject = (
  obj: Record<string, unknown>,
  prefix = ""
): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      result[path] = value;
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(
        result,
        flattenTranslationObject(value as Record<string, unknown>, path)
      );
    }
  }

  return result;
};

const buildTemplateMatcher = (
  localizedTemplate: string,
  englishTemplate: string
): ToastTemplateMatcher | null => {
  const varNames: string[] = [];
  const placeholderRegex = /\{\{(\w+)\}\}/g;
  let regexStr = "^";
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = placeholderRegex.exec(localizedTemplate)) !== null) {
    regexStr += escapeRegExp(localizedTemplate.slice(lastIndex, match.index));
    regexStr += "(.+?)";
    varNames.push(match[1]);
    lastIndex = match.index + match[0].length;
  }

  if (varNames.length === 0) return null;

  regexStr += escapeRegExp(localizedTemplate.slice(lastIndex)) + "$";

  return {
    regex: new RegExp(regexStr),
    englishTemplate,
    varNames,
  };
};

const applyEnglishTemplate = (
  englishTemplate: string,
  varNames: string[],
  capturedValues: string[],
  exactMap: Map<string, string>
): string => {
  let result = englishTemplate;

  varNames.forEach((varName, index) => {
    const captured = capturedValues[index]?.trim() ?? "";
    const englishValue = exactMap.get(captured) ?? captured;
    result = result.replace(
      new RegExp(`\\{\\{${varName}\\}\\}`, "g"),
      englishValue
    );
  });

  return result;
};

const buildToastEnglishResolverCache = (): ToastEnglishResolverCache => {
  const exactMap = new Map<string, string>();
  const templateMatchers: ToastTemplateMatcher[] = [];
  const resources = i18n.options.resources ?? {};
  const englishResources = resources.en ?? {};

  for (const [language, namespaces] of Object.entries(resources)) {
    if (language === "en" || !namespaces || typeof namespaces !== "object") {
      continue;
    }

    for (const [namespace, localizedNamespace] of Object.entries(namespaces)) {
      const englishNamespace = englishResources[namespace];
      if (
        !localizedNamespace ||
        typeof localizedNamespace !== "object" ||
        !englishNamespace ||
        typeof englishNamespace !== "object"
      ) {
        continue;
      }

      const localizedFlat = flattenTranslationObject(
        localizedNamespace as Record<string, unknown>
      );
      const englishFlat = flattenTranslationObject(
        englishNamespace as Record<string, unknown>
      );

      for (const [keyPath, localizedValue] of Object.entries(localizedFlat)) {
        const englishValue = englishFlat[keyPath];
        if (!englishValue || localizedValue === englishValue) continue;

        const localizedText = localizedValue.trim();
        const englishText = englishValue.trim();
        exactMap.set(localizedText, englishText);

        const matcher = buildTemplateMatcher(localizedText, englishText);
        if (matcher) templateMatchers.push(matcher);
      }
    }
  }

  return { exactMap, templateMatchers };
};

const getToastEnglishResolverCache = (): ToastEnglishResolverCache => {
  if (!toastEnglishResolverCache) {
    toastEnglishResolverCache = buildToastEnglishResolverCache();
  }
  return toastEnglishResolverCache;
};

/**
 * Maps a localized toast message back to its English equivalent using i18n resources.
 * Falls back to the original message when no translation mapping exists.
 */
export const resolveToastMessageToEnglish = (message: unknown): string => {
  const messageStr = String(message ?? "").trim();
  if (!messageStr) return messageStr;

  const { exactMap, templateMatchers } = getToastEnglishResolverCache();
  const exactMatch = exactMap.get(messageStr);
  if (exactMatch) return exactMatch;

  for (const matcher of templateMatchers) {
    const match = messageStr.match(matcher.regex);
    if (!match) continue;

    return applyEnglishTemplate(
      matcher.englishTemplate,
      matcher.varNames,
      match.slice(1),
      exactMap
    );
  }

  return messageStr;
};

export const getEnglishToastMessage = (
  key: string,
  params?: Record<string, unknown>,
  namespace = "common"
): string => {
  const resolvedParams = params
    ? Object.fromEntries(
      Object.entries(params).map(([paramKey, value]) => [
        paramKey,
        typeof value === "string"
          ? resolveToastMessageToEnglish(value)
          : value,
      ])
    )
    : params;

  return i18n.getFixedT("en", namespace)(key, resolvedParams);
};

export const resolveToastMessage = (
  payload: unknown,
  englishDefaultKey: string,
  params?: Record<string, unknown>,
  customMessage?: string
): string =>
  extractApiMessage(payload, customMessage) ??
  getEnglishToastMessage(englishDefaultKey, params);

/**
 * Displays toast notifications using react-toastify with consistent styling
 *
 * @param message - The message text to display
 * @param type - The type of toast (error, warning, or success)
 * @param duration - How long to show the toast in milliseconds (default: 3000ms)
 * @returns void
 *
 * @example
 * ```typescript
 * showToast("Operation completed successfully!", "success");
 * showToast("Please check your input", "warning", 5000);
 * showToast("An error occurred", "error");
 * ```
 */
import React from "react"; // add this at the top with other imports

// Store the last toast info to prevent rapid duplicates
let lastToast: { message: string; type: string; timestamp: number } | null = null;
const TOAST_DEBOUNCE_MS = 500;

export const showToast = (
  message: String,
  type: "error" | "warning" | "success" | "info",
  duration = 3000
) => {
  const messageStr = resolveToastMessageToEnglish(message);
  const now = Date.now();

  // Skip duplicate toasts within debounce window
  if (
    lastToast &&
    lastToast.message === messageStr &&
    lastToast.type === type &&
    now - lastToast.timestamp < TOAST_DEBOUNCE_MS
  ) {
    return;
  }

  const normalizedMessage = messageStr.replace(/\s+/g, " ").trim().slice(0, 100);
  const toastId = `${type}-${normalizedMessage}`;

  lastToast = { message: messageStr, type, timestamp: now };

  toast.dismiss(toastId);

  const headingMap: Record<typeof type, { label: string; color: string; bg: string }> = {
    success: { label: "Success", color: toastSuccessText, bg: toastSuccessBg },
    error: { label: "Error", color: toastErrorText, bg: toastErrorBg },
    warning: { label: "Warning", color: toastWarningText, bg: toastWarningBg },
    info: { label: "Info", color: toastInfoText, bg: toastInfoBg },
  };

  const config = headingMap[type] || headingMap.info;
  const iconChar = type === "success" ? "✓" : "!";

  const content = React.createElement(
    "div",
    { style: { display: "flex", flexDirection: "column", gap: 4 } },
    React.createElement(
      "div",
      { style: { display: "flex", alignItems: "center", gap: 10 } },
      // Icon circle
      React.createElement(
        "div",
        {
          className: "custom-toast-icon",
          style: {
            width: 26, height: 26,
            borderRadius: "999px",
            backgroundColor: config.bg,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          },
        },
        React.createElement(
          "span",
          { style: { fontSize: 16, color: config.color, fontWeight: 700 } },
          iconChar
        )
      ),
      // Text block
      React.createElement(
        "div",
        { style: { display: "flex", flexDirection: "column", gap: 1 } },
        React.createElement(
          "div",
          {
            className: "custom-toast-heading",
            style: { fontSize: 15, fontWeight: 600, color: toastHeadingText },
          },
          config.label
        ),
        React.createElement(
          "div",
          {
            className: "custom-toast-body",
            style: { fontSize: 13, color: toastBodyText },
          },
          messageStr
        )
      )
    )
  );

  toast(content, {
    type,
    position: "top-right",
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    className: `custom-toast ${type}`,
    toastId,
    icon: false,
    style: {
      borderRadius: "8px",
      padding: "3px 4px",
    },
  });
};

export const showEnglishToast = (
  key: string,
  type: "error" | "warning" | "success" | "info",
  params?: Record<string, unknown>,
  namespace = "common",
  duration = 3000
) => {
  showToast(getEnglishToastMessage(key, params, namespace), type, duration);
};

/**
 * Converts seconds into a human-readable time format
 *
 * @param timeInSeconds - Time duration in seconds
 * @returns Formatted time string (e.g., "2h:30m:45s", "45m:30s", "30s")
 *
 * @example
 * ```typescript
 * formatTime(3665);    // Returns "1h:1m:5s"
 * formatTime(125);     // Returns "2m:5s"
 * formatTime(45);      // Returns "45s"
 * formatTime(0);       // Returns ""
 * ```
 */
export const formatTime = (timeInSeconds: number) => {
  let hours = 0;
  let minutes = 0;
  let seconds = 0;

  // Calculate hours if time is greater than 1 hour
  if (timeInSeconds > 3600) {
    while (timeInSeconds >= 3600) {
      hours += 1;
      timeInSeconds -= 3600;
    }
  }

  // Calculate minutes if remaining time is greater than 1 minute
  if (timeInSeconds > 60) {
    while (timeInSeconds >= 60) {
      minutes += 1;
      timeInSeconds -= 60;
    }
  }

  // Remaining time becomes seconds
  seconds = timeInSeconds;

  // Build formatted string, only showing relevant units
  return (
    (hours > 0 ? `${hours}h:` : "") +
    (hours > 0 || minutes > 0 ? `${minutes}m:` : "") +
    (minutes > 0 || seconds > 0 ? `${seconds}s` : "")
  );
};
/**
 * Formats a date/datetime string to "DD MMM, YYYY HH:mm:ss" format.
 *
 * @param dateStr - Date string (ISO datetime or YYYY-MM-DD)
 * @returns Formatted date string or empty string if invalid
 *
 * @example
 * ```typescript
 * formatDate("2026-05-13T06:01:06");  // Returns "13 May, 2026 06:01:06"
 * formatDate("2024-01-15");           // Returns "15 Jan, 2024 00:00:00"
 * formatDate("");                     // Returns ""
 * ```
 */
export const formatDate = (dateStr: string) => {
  if (!dateStr) return "";

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    showToast("Date format is incorrect", "warning");
    return "";
  }

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const pad = (value: number) => String(value).padStart(2, "0");

  return `${pad(date.getDate())} ${months[date.getMonth()]}, ${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};
/**
 * Converts an array of objects to CSV format string
 *
 * @param array - Array of objects to convert to CSV
 * @returns CSV formatted string
 * @throws {Error} Throws error if array is empty or objects don't have consistent keys
 *
 * @example
 * ```typescript
 * const data = [
 *   { name: "John", age: 30, city: "NYC" },
 *   { name: "Jane", age: 25, city: "LA" }
 * ];
 * const csv = convertArrayOfObjectsToCSV(data);
 * // Returns: "name,age,city\nJohn,30,NYC\nJane,25,LA"
 * ```
 */
export const convertArrayOfObjectsToCSV = (array: Record<string, any>[]) => {
  const columnDelimiter = ",";
  const lineDelimiter = "\n";
  const keys = Object.keys(array[0]);

  // Create CSV header row from object keys
  let csv = keys.join(columnDelimiter) + lineDelimiter;

  // Create CSV data rows from object values
  array.forEach((item) => {
    csv +=
      keys.map((key) => '"' + item[key] + '"').join(columnDelimiter) +
      lineDelimiter;
  });

  return csv;
};

/**
 * Downloads an array of objects as a CSV file
 *
 * @param array - Array of objects to download
 * @param filename - Name of the downloaded file (default: "Report.csv")
 * @returns void
 *
 * @example
 * ```typescript
 * const reportData = [
 *   { date: "2024-01-01", revenue: 1000, profit: 200 },
 *   { date: "2024-01-02", revenue: 1200, profit: 250 }
 * ];
 * downloadCSV(reportData, "monthly_report.csv");
 * ```
 */
export const downloadCSV = (
  array: Record<string, any>[],
  filename: string = "Report.csv"
) => {
  // Convert array to CSV format
  const csv = convertArrayOfObjectsToCSV(array);

  // Create blob with CSV content
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  // Create temporary download link
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.style.display = "none";

  // Append link to DOM, trigger download, and clean up
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
/**
 * Checks if wallet minimum flag is enabled
 *
 * @param flag - Flag value ("YES" or "NO")
 * @returns Boolean indicating if wallet minimum is enabled
 *
 * @example
 * ```typescript
 * checkForWalletMin("YES");   // Returns true
 * checkForWalletMin("min");   // Returns true
 * checkForWalletMin("NO");    // Returns false
 * ```
 */
export function checkForWalletMin(flag: "YES" | "NO") {
  return flag && (flag.toLowerCase() === "yes" || flag.toLowerCase() === "min");
}

/**
 * Saves a search value for a specific module in localStorage
 *
 * @param module - Module name to save search for
 * @param searchVal - Search value to save
 * @returns void
 *
 * @example
 * ```typescript
 * saveSearch("users", "john");
 * saveSearch("reports", "monthly");
 * ```
 */
export function saveSearch(module: string, searchVal: string) {
  if (searchVal === "") return;

  // Get existing saved searches or initialize empty object
  let localSearch = getItem("savedSearch", true) || {};

  // Check if module already has saved searches
  if (localSearch[module] && Array.isArray(localSearch[module])) {
    // Add new search value if it doesn't already exist
    if (!localSearch[module].includes(searchVal))
      localSearch[module] = [...localSearch[module], searchVal];
  } else {
    // Initialize module with new search value
    localSearch[module] = [searchVal];
  }

  // Save updated searches back to localStorage
  storeItem("savedSearch", localSearch);
}

/**
 * Retrieves saved search values for a specific module
 *
 * @param module - Module name to get searches for
 * @returns Array of saved search values for the module
 *
 * @example
 * ```typescript
 * const userSearches = getSearch("users");     // Returns ["john", "jane", "admin"]
 * const reportSearches = getSearch("reports"); // Returns ["monthly", "quarterly"]
 * ```
 */
export function getSearch(module: string) {
  let localSearch = getItem("savedSearch", true);
  if (localSearch && typeof localSearch === "object")
    return localSearch[module] ?? [];
  else return [];
}
/**
 * Type checking utility functions for common data types
 */

/**
 * Checks if a value is an array
 *
 * @param val - Value to check
 * @returns Boolean indicating if value is an array
 *
 * @example
 * ```typescript
 * isArray([1, 2, 3]);        // Returns true
 * isArray("string");          // Returns false
 * isArray({ key: "value" });  // Returns false
 * ```
 */
export const isArray = (val: any) => {
  return val && typeof val === "object" && Array.isArray(val);
};

/** Show API values in tables without i18n (booleans, strings, etc.). */
export const formatApiDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return String(value);
  const str = String(value).trim();
  return str === "" ? "-" : str;
};

/** Truthiness for API boolean-like values when styling cells. */
export const isApiTruthy = (value: unknown): boolean => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized === "true" || normalized === "yes" || normalized === "1";
};

/**
 * Checks if a value is a valid non-empty string
 *
 * @param val - Value to check
 * @returns Boolean indicating if value is a valid string
 *
 * @example
 * ```typescript
 * isValidString("hello");     // Returns true
 * isValidString("");          // Returns false
 * isValidString("   ");       // Returns false
 * isValidString(null);        // Returns false
 * ```
 */
export const isValidString = (val: any) => {
  return val && val.trim() !== "";
};

/**
 * Checks if a value is an object (but not null or array)
 *
 * @param val - Value to check
 * @returns Boolean indicating if value is an object
 *
 * @example
 * ```typescript
 * isObject({ key: "value" }); // Returns true
 * isObject([1, 2, 3]);        // Returns false
 * isObject(null);              // Returns false
 * isObject("string");          // Returns false
 * ```
 */
export const isObject = (val: any) => {
  return val && typeof val === "object";
};

/**
 * Safely checks if an object has a specific property
 *
 * @param obj - Object to check
 * @param key - Property key to check for
 * @returns Boolean indicating if property exists
 *
 * @example
 * ```typescript
 * hasProperty({ name: "John" }, "name");     // Returns true
 * hasProperty({ name: "John" }, "age");      // Returns false
 * hasProperty(null, "key");                  // Returns false
 * ```
 */
export const hasProperty = (obj: Record<string, any>, key: string) => {
  if (!isObject(obj) || !obj || typeof obj.hasOwnProperty !== "function")
    return false;
  return obj.hasOwnProperty(key);
};

/**
 * Checks if an object is of a specific type
 *
 * @param obj - Object to check
 * @param value - Expected type as string
 * @returns Boolean indicating if object matches the expected type
 *
 * @example
 * ```typescript
 * kindOf("string", "string");        // Returns true
 * kindOf(123, "number");             // Returns true
 * kindOf([1, 2, 3], "object");      // Returns true
 * ```
 */
export const kindOf = (obj: Record<string, any>, value: string) => {
  return obj && typeof obj === value.toLowerCase();
};
/**
 * Adds time to date strings in a filter data object based on a given offset.
 *
 * @param filterData - The filter data object containing date strings.
 * @param offset - The time zone offset in minutes.
 * @returns A new object with updated date strings, including time components.
 * 
 * @example
 * ```typescript
 * const filterData = { startDate: "2024-01-01", endDate: "2024-01-02" };
 * const offset = 330;
 * const filteredData = addTimeInDates(filterData, offset); // Returns { startDate: "2024-01-01 00:00:00", endDate: "2024-01-02 23:59:59" }
 * ```
 */
export const addTimeInDates = (
  filterData: Record<string, any>,
  offset: number
) => {
  return {
    ...filterData,
    ...(filterData.startDate
      ? {
        startDate: `${getDateInTimeZone(
          offset,
          filterData.startDate?.split(" ")[0]
        )} 00:00:00`,
      }
      : {}),
    ...(filterData.endDate
      ? {
        endDate: `${getDateInTimeZone(
          offset,
          filterData.endDate?.split(" ")[0]
        )} 23:59:59`,
      }
      : {}),
  };
};
/**
 * Filters out null values from an object, except for specified keys.
 *
 * @param obj - The object to filter.
 * @param toInclude - An array of keys to include in the output.
 * @returns A new object with null values filtered out, except for the specified keys.
 * 
 * @example
 * ```typescript
 * const obj = { a: 1, b: null, c: 3 };
 * const filtered = skipNullValue(obj, ["a", "c"]); // { a: 1, c: 3 }
 * ```
 */
export const skipNullValue = (
  obj: Record<string, any>,
  toInclude: any[] = []
) => {
  let body: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    if (!toInclude.includes(obj[key])) {
      if (!key.includes("search"))
        if ((obj[key] !== undefined || obj[key] !== null) && obj[key] !== "")
          body[key] = obj[key];
    } else body[key] = obj[key];
  });

  return body;
};
/**
 * Converts a string to a Word document and saves it to a file.
 *
 * @param value - The string to convert to a Word document.
 * @returns void
 *
 * @example
 * ```typescript
 * dataToDocx("Hello, world!");
 * ```
 */
export const dataToDocx = (value: string) => {
  if (value === "") {
    return;
  }
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            children: [new TextRun(value)],
          }),
        ],
      },
    ],
  });
  saveDocumentToFile(doc, `converter.docx`);
};
/**
 * Saves a document to a file.
 *
 * @param doc - The document to save.
 * @param fileName - The name of the file to save.
 * @returns void
 *
 * @example
 * ```typescript
 * const doc = new Document({ sections: [{ children: [new TextRun("Hello, world!")] }] });
 * saveDocumentToFile(doc, "example.docx");
 * ```
 */
function saveDocumentToFile(doc: DocxFile, fileName: string) {
  const mimeType =
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  Packer.toBlob(doc).then((blob) => {
    const docblob = blob.slice(0, blob.size, mimeType);
    saveAs(docblob, fileName);
  });
}
/**
 * User Role Management Functions
 *
 * These functions provide utilities for checking user roles and permissions
 * throughout the SMPP application. They handle different user role structures
 * and provide convenient role-checking methods.
 */

/**
 * Checks if a user has a specific role
 *
 * @param user - User object to check
 * @param role - Role to check for
 * @returns Boolean indicating if user has the specified role
 *
 * @example
 * ```typescript
 * const user = { roles: ["admin", "user"] };
 * checkUserRole(user, "admin");  // Returns true
 * checkUserRole(user, "seller"); // Returns false
 * ```
 */
export const checkUserRole = (user: any, role: string) => {
  if (user && user.roles && isArray(user.roles)) {
    return user.roles.includes(role);
  } else if (user && user.role && typeof user.role === "string") {
    return user.role.includes(role);
  } else return false;
};

/**
 * Convenience function to check if user has "user" role
 */
export const isUser = (user: any) => checkUserRole(user, "user");

/**
 * Convenience function to check if user has "tenant" role
 */
export const isTenant = (user: any) => checkUserRole(user, "tenant");

/**
 * Convenience function to check if user has "admin" or "tenant" role.
 * Do not use alone when tenant must be treated differently from admin.
 */
export const isAdmin = (user: any) =>
  checkUserRole(user, "admin") || checkUserRole(user, "tenant");

/**
 * Convenience function to check if user has "system" role
 */
export const isSystem = (user: any) => checkUserRole(user, "system");

/**
 * Convenience function to check if user has "seller" role
 */
export const isSeller = (user: any) => checkUserRole(user, "seller");

/**
 * Convenience function to check if user has "manager" role
 */
export const isManager = (user: any) => checkUserRole(user, "manager");

/**
 * Convenience function to check if user has "superadmin" role
 */
export const isSuperAdmin = (user: any) => checkUserRole(user, "superadmin");

/**
 * Checks if user is a guest (no roles assigned)
 *
 * @param user - User object to check
 * @returns Boolean indicating if user is a guest
 */
export const isGuest = (user: any) => !(user && user.roles && user.roles[0]);

/**
 * Checks if user has any of the specified roles
 *
 * @param rolesString - Comma-separated string of roles to check
 * @param user - User object to check
 * @returns Boolean indicating if user has any of the specified roles
 *
 * @example
 * ```typescript
 * const user = { roles: ["admin", "user"] };
 * hasRoles("admin,seller", user);  // Returns true (has admin)
 * hasRoles("seller,manager", user); // Returns false (has neither)
 * hasRoles("admin", { roles: ["tenant"] }); // Returns true (admin aliases tenant)
 * ```
 */
export const hasRoles = (rolesString: string, user: any) => {
  const roles = rolesString
    .split(",")
    .map((r) => r.trim())
    .filter(Boolean);
  return roles.some((role) => {
    const normalized = role.toLowerCase();
    // Same privilege tier as isAdmin(): tenant is treated as admin in role gates.
    if (normalized === "admin") {
      return (
        checkUserRole(user, "admin") || checkUserRole(user, "tenant")
      );
    }
    return checkUserRole(user, role);
  });
};

/**
 * Checks if user has any role assigned
 *
 * @param user - User object to check
 * @returns Boolean indicating if user has any role
 */
export const hasRole = (user: any) => {
  return (
    user &&
    ((user.roles && user.roles[0]) ||
      (user.role && typeof user.role === "string"))
  );
};
/**
 * Filters data based on a field and search value.
 *
 * @param data - The array of data to filter.
 * @param fields - The field or fields to filter by.
 * @param searchVal - The value to search for.
 * @param subKey - The sub-key to filter by (optional).
 * @param valueMap - A map of field names to functions for value transformation (optional).
 * @returns The filtered data.
 *
 * @example
 * ```typescript
 * const filteredData = filterDataByField(data, "name", "John"); // Returns data with name "John"
 * ```
 */
export const filterDataByField = (
  data: any[],
  fields: string | string[],
  searchVal: string | number | boolean | object,
  subKey?: string,
  valueMap?: Map<string, string>
) => {
  if (searchVal === "" || !searchVal) return data;
  if (typeof searchVal !== "string")
    searchVal = searchVal.toString ? searchVal.toString() : String(searchVal);

  fields = typeof fields === "string" ? fields.split(",") : [];
  return data.filter((row) => {
    let bool = false;
    fields.forEach((field) => {
      let value =
        valueMap && typeof valueMap[field] === "function"
          ? valueMap[field](row[field])
          : subKey
            ? row[subKey] && row[subKey][field]
            : row[field];
      if (
        value &&
        value.toString().toLowerCase().includes(searchVal.trim().toLowerCase())
      ) {
        bool = true;
      }
    });
    return bool;
  });
};
/**
 * Converts a value to an integer.
 *
 * @param value - The value to convert.
 * @returns The integer value if conversion is successful, otherwise NaN.
 *
 * @example
 * ```typescript
 * const int = convertToInt(123); // Returns 123
 * const int2 = convertToInt("123.45"); // Returns NaN
 * const int3 = convertToInt(true); // Returns 1
 * ```
 */
export const convertToInt = (value: any) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number.isInteger(value) ? +value : NaN;
  if (typeof value === "boolean") return Number(value);
  if (typeof value === "object" && Array.isArray(value))
    return value.map((v) => convertToInt(v));
  else NaN;
};
export const someTrue = (obj: any, values: string | undefined | null) => {
  if (!obj || !values || values === "") return true;
  let bool = false;
  for (let i = 0; i < values.split(",").length; i++) {
    let v = values.split(",")[i];
    if (obj[v]) {
      bool = true;
      break;
    }
  }
  return bool;
};
export const numberFromFile = async (
  items: File[],
  options: { columnIndex?: number; delim?: string } = {}
) => {
  let { columnIndex = 0, delim } = options;
  let totalNum = 0;

  for (const item of items) {
    const fileName = item.name;
    if (
      !fileName ||
      (!fileName.endsWith(".txt") &&
        !fileName.endsWith(".csv") &&
        !fileName.endsWith(".xls") &&
        !fileName.endsWith(".xlsx"))
    ) {
      continue;
    }
    try {
      const content = await item.arrayBuffer();

      if (fileName.endsWith(".txt") || fileName.endsWith(".csv")) {
        totalNum += processTextFile(
          content,
          fileName.toLowerCase().includes("custom") ||
            fileName.toLowerCase().includes("bulkbysender")
            ? fileName.endsWith(".txt")
              ? delim ?? ";"
              : delim ?? ","
            : false,
          columnIndex
        );
      } else {
        totalNum += await processExcelFile(content, fileName, columnIndex);
      }
    } catch (e) {
      console.error("Error processing file: " + fileName, e);
      throw new Error("Error processing files " + e.getMessage());
    }
  }

  return totalNum;
};
function processTextFile(
  content: any,
  custom: boolean | string,
  numberIndex?: number
) {
  const decoder = new TextDecoder("utf-8");
  const text = decoder.decode(content);
  const lines = text.split("\n");
  let numCount = 0;

  for (const line of lines) {
    const number = validateNumber(line, custom, numberIndex);
    if (number !== null) {
      numCount++;
    }
  }
  return numCount;
}
/**
 * Processes an Excel file and counts the number of valid numbers in the specified column.
 *
 * @param content - The content of the Excel file.
 * @param fileName - The name of the Excel file.
 * @param index - The index of the column to process (default: 0).
 * @returns The number of valid numbers in the specified column.
 *
 * @example
 * ```typescript
 * const numCount = processExcelFile(content, "file.xlsx", 0); // Returns 100
 * ```
 */
async function processExcelFile(content: any, fileName: string, index = 0) {
  const workbook = fileName.endsWith(".xlsx")
    ? XLSX.read(new Uint8Array(content), { type: "array" })
    : XLSX.read(new Uint8Array(content), { type: "binary" });

  const sheet: { [sheet: string]: XLSX.WorkSheet } =
    workbook.Sheets[workbook.SheetNames[0]];
  const data: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

  let numCount = 0;

  for (const row of data) {
    if (row[index]) {
      const cellValue = row[index].toString();
      const number = validateNumber(cellValue);
      if (number !== null) {
        numCount++;
      }
    }
  }
  return numCount;
}
/**
 * Validates a number string and returns the number if valid, otherwise returns null.
 *
 * @param number - The number string to validate.
 * @param isCustom - Whether the number is from a custom file (default: false).
 * @param numberIndex - The index of the number in the custom file (default: 0).
 * @returns The validated number if valid, otherwise null.
 *
 * @example
 * ```typescript
 * const number = validateNumber("123"); // Returns "123"
 * const number2 = validateNumber("123.45"); // Returns "123.45"
 * const number3 = validateNumber("abc"); // Returns null
 * ```
 */
function validateNumber(
  number: string | number,
  isCustom?: boolean | string,
  numberIndex?: number
) {
  try {
    if (typeof number === "string") {
      if (isCustom && typeof isCustom === "string") {
        number = number.split(isCustom)[numberIndex ?? 0];
      }
      number = number.replace(/\s+/g, "");
      if (number.startsWith("+")) {
        number = number.substring(number.lastIndexOf("+") + 1);
      }
    }
    if (number === "") return null;

    number = String(Number(number));
    if (number !== "NaN") return number;
    else return null;
  } catch (ex) {
    console.error("Invalid Destination Found => " + number);
    return null;
  }
}
/**
 * Extracts the number of parameters from a custom file.
 *
 * @param items - An array of File objects.
 * @returns The number of parameters in the first line of the file.
 *
 * @example
 * ```typescript
 * const params = getParamsFromCustomFile([new File([], "file.txt")]); // Returns 3
 * ```
 */
export async function getParamsFromCustomFile(items: File[]) {
  let item = items[0];
  let fileName = item.name;
  const content = await item.arrayBuffer();
  const decoder = new TextDecoder("utf-8");
  const text = decoder.decode(content);
  const lines = text.split("\n");

  if (fileName.endsWith(".txt")) {
    return lines[0].split(";").length - 1;
  } else if (fileName.endsWith(".csv")) {
    return lines[0].split(",").length - 1;
  } else if (fileName.endsWith(".txt")) {
    const workbook = fileName.endsWith(".xlsx")
      ? XLSX.read(new Uint8Array(content), { type: "array" })
      : XLSX.read(new Uint8Array(content), { type: "binary" });

    const sheet: { [sheet: string]: XLSX.WorkSheet } =
      workbook.Sheets[workbook.SheetNames[0]];
    const data: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    return data[0].length - 1;
  }
}
/**
 * Downloads a file from a given path with a specified filename.
 *
 * @param path - The URL or path of the file to download.
 * @param filename - The name of the file to be downloaded.
 * @returns void
 *
 * @example
 * ```typescript
 * downloadFile("https://example.com/file.pdf", "example.pdf");
 * ```
 */
export function downloadFile(path: string, filename: string) {
  const link = document.createElement("a");
  link.href = path;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
/**
 * Returns the browser's GMT offset in hours or a formatted GMT string.
 *
 * @param inHours - Whether to return the offset in hours (default: false).
 * @returns The GMT offset in hours if inHours is true, otherwise a formatted GMT string.
 *
 * @example
 * ```typescript
 * const offset = getBrowserGMTOffset(true); // Returns 5.5
 * const offset2 = getBrowserGMTOffset(); // Returns "GMT+5:30#1"
 * ```
 */
export const getBrowserGMTOffset = (inHours?: boolean) => {
  const currentDate = new Date();
  const timezoneOffsetInMinutes = currentDate.getTimezoneOffset();

  const offsetHours = Math.floor(Math.abs(timezoneOffsetInMinutes) / 60);
  const offsetMinutes = Math.abs(timezoneOffsetInMinutes) % 60;
  const offsetSign = timezoneOffsetInMinutes <= 0 ? "+" : "-";

  if (inHours)
    return (offsetHours + offsetMinutes / 60) * (offsetSign === "+" ? 1 : -1);
  const formattedOffset = `GMT${offsetSign}${String(offsetHours).padStart(
    2,
    "0"
  )}:${String(offsetMinutes).padStart(2, "0")}#1`;

  return formattedOffset;
};
/**
 * Converts a date string to a date string in a specific time zone.
 *
 * @param offset - The time zone offset in minutes.
 * @param dateString - The date string to convert (e.g., "2025-05-03").
 * @returns The converted date string in the specified time zone.
 *
 * @example
 * ```typescript
 * const date = getDateInTimeZone(330, "2025-05-03"); // Returns "2025-05-03"
 * ```
 */
export function getDateInTimeZone(offset: number, dateString: string) {
  let [dateYear, dateMonth, dateDay] = dateString.split("-").map((p) => +p);
  let date = new Date();
  date.setUTCFullYear(dateYear);
  date.setUTCMonth(dateMonth - 1);
  date.setUTCDate(dateDay);

  let dateInTimeZone = new Date(date.getTime() + offset * 60000);

  let year = dateInTimeZone.getUTCFullYear();
  let month = String(dateInTimeZone.getUTCMonth() + 1).padStart(2, "0");
  let day = String(dateInTimeZone.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}
/**
 * Converts a date string to a date-time string in a specific time zone.
 *
 * @param offset - The time zone offset in minutes.
 * @param dateString - The date string to convert (e.g., "2025-05-03T14:30").
 * @param dateSeperator - The separator between the date and time parts (default: " ").
 * @returns The converted date-time string in the specified time zone.
 *
 * @example
 * ```typescript
 * const dateTime = getDateTimeInTimeZone(330, "2025-05-03T14:30"); // Returns "2025-05-03 14:30:00"
 * ```
 */
export function getDateTimeInTimeZone(
  offset: number,
  dateString: string,
  dateSeperator = " "
) {
  // Parse dateString like "2025-05-03T14:30"
  const [datePart, timePart] = dateString.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, seconds] = timePart.split(":").map(Number);

  const localOffset = +getBrowserGMTOffset(true) * 60;

  const utcDate = new Date(year, month - 1, day, hour, minute, seconds);
  const localDate = new Date(utcDate.getTime() - localOffset * 60000);
  const shiftedDate = new Date(localDate.getTime() + offset * 60000);

  const yyyy = shiftedDate.getFullYear();
  const MM = String(shiftedDate.getMonth() + 1).padStart(2, "0");
  const dd = String(shiftedDate.getDate()).padStart(2, "0");
  const HH = String(shiftedDate.getHours()).padStart(2, "0");
  const mm = String(shiftedDate.getMinutes()).padStart(2, "0");
  const ss = String(shiftedDate.getSeconds()).padStart(2, "0");

  return `${yyyy}-${MM}-${dd}${dateSeperator}${HH}:${mm}:${ss}`;
}
/**
 * Converts a GMT (Greenwich Mean Time) string to an offset in minutes.
 *
 * @param gmt - The GMT string to convert (e.g., "GMT+5:30" or "GMT-6:00").
 * @returns The offset in minutes.
 *
 * @example
 * ```typescript
 * const offset = convertGmtToOffest("GMT+5:30"); // Returns 330
 * const offset2 = convertGmtToOffest("GMT-6:00"); // Returns -360
 * ```
 */
export function convertGmtToOffest(gmt: string) {
  let [hour = "0", minutes = "0"] = gmt
    .replace("GMT", "")
    .split("#")[0]
    .split(":");
  return +hour * 60 + +minutes;
}
/**
 * Capitalizes the first letter of a string.
 *
 * @param val - The string to capitalize.
 * @returns The string with the first letter capitalized, or an empty string if the input is not a string.
 *
 * @example
 * ```typescript
 * capitalize("hello"); // Returns "Hello"
 * capitalize("world"); // Returns "World"
 * capitalize(123);     // Returns ""
 * ```
 */
export function capitalize(val: any) {
  if (!val || typeof val !== "string") return "";
  return val.substring(0, 1).toUpperCase() + val.substring(1);
}
/**
 * Converts a byte value to a human-readable string with appropriate units (KB, MB, GB).
 *
 * @param val - The byte value to convert.
 * @returns A string representing the byte value with appropriate units (e.g., "10.25MB").
 *
 * @example
 * ```typescript
 * convertBytesToString(1024); // Returns "1KB"
 * convertBytesToString(1024 * 1024); // Returns "1MB"
 * convertBytesToString(1024 * 1024 * 1024); // Returns "1GB"
 * ```
 */
export function convertBytesToString(val: string | number): string {
  let level = 0;
  if (typeof val === "string")
    if (!Number.isNaN(val)) val = +val;
    else return `0${sizesArray[level]}`;

  while (val >= 1024 && level < sizesArray.length - 1) {
    val /= 1024;
    level++;
  }

  return `${(val ?? 0).toFixed(2)}${sizesArray[level]}`;
}
/**
 * Progress Management Functions
 *
 * These functions control the NProgress bar for user feedback during operations.
 */

/**
 * Starts the progress bar with optional auto-stop functionality
 *
 * @param stop - Whether to automatically stop the progress bar after timeout
 * @param timeout - Time in milliseconds before auto-stopping (default: 400ms)
 * @returns void
 *
 * @example
 * ```typescript
 * startProgress();           // Starts progress, auto-stops after 400ms
 * startProgress(true, 1000); // Starts progress, auto-stops after 1 second
 * startProgress(false);      // Starts progress, manual stop required
 * ```
 */
export function startProgress(stop: boolean = true, timeout: number = 400) {
  NProgress.start();

  if (stop) {
    setTimeout(() => {
      NProgress.done();
    }, timeout);
  }
}

/**
 * Stops the progress bar immediately
 *
 * @returns void
 *
 * @example
 * ```typescript
 * startProgress(false); // Start progress without auto-stop
 * // ... perform operation ...
 * stopProgress();       // Manually stop progress
 * ```
 */
export function stopProgress() {
  NProgress.done();
}
/**
 * Modal Management Functions
 *
 * These functions handle modal state through URL query parameters,
 * allowing modals to be shareable and bookmarkable.
 */

/**
 * Opens a modal by adding a query parameter to the current URL
 *
 * @param modalName - Name of the modal to open (becomes query parameter)
 * @param navigate - Navigation function to update the URL
 * @returns void
 *
 * @example
 * ```typescript
 * openModalQuery("userModal", navigate);
 * // Results in URL: /current/path?userModal=true
 * ```
 */
export function openModalQuery(
  modalName: string,
  navigate: (pathName: string) => void
) {
  if (!modalName || modalName === "") return;
  navigate(`${window.location.pathname}?${modalName}=true`);
}

/**
 * Closes all modals by removing query parameters from the URL
 *
 * @returns void
 *
 * @example
 * ```typescript
 * closeModalQuery();
 * // Changes URL from /current/path?modal=true to /current/path
 * ```
 */
export function closeModalQuery() {
  window.history.pushState({}, "", window.location.pathname);
}
export function getStylesForStatus(status: string | undefined) {
  if (!status || status === "")
    return {
      color: "white",
      backgroung: ChartColors.advertising,
    };

  return {
    color: "white",
    background: ChartColors.advertising,
    //add this for back the text into "ESME_RINVCOST"
    ...(status === "ESME_RINVCOST"
      ? { background: ChartColors.advertising, color: black }
      : {}),
    ...(status === "DELIVRD"
      ? { background: ChartColors.delivered, color: paginationText }
      : {}),
    ...(status === "REJECT" || status === "REJECTED" || status === "REJECTD"
      ? { background: ChartColors.rejected, color: paginationText }
      : {}),
    ...(status === "Success"
      ? { background: ChartColors.delivered, color: paginationText }
      : {}),
    ...(status === "UNDELIV"
      ? { background: ChartColors.undelivered, color: paginationText }
      : {}),
    ...(status === "EXPIRED"
      ? { background: ChartColors.expired, color: paginationText }
      : {}),
    ...(status === "BLOCKED"
      ? { background: ChartColors.blocked, color: paginationText }
      : {}),
    ...(status === "ATES"
      ? { background: ChartColors.ates, color: paginationText }
      : {}),
    ...(status === "ACCEPTD"
      ? { background: ChartColors.accepted, color: paginationText }
      : {}),
    ...(status === "PENDING"
      ? { background: ChartColors.pending, color: paginationText }
      : {}),
    ...(status === "QUEUED" || status === "Q"
      ? { background: ChartColors.queued, color: paginationText }
      : {}),
    ...(status === "UNPROCD"
      ? { background: ChartColors.unprocessed, color: paginationText }
      : {}),
  };
}
/**
 * Filters out blank fields from an object, returning a new object with only non-empty values.
 *
 * @param obj - The object to filter.
 * @returns A new object with only non-empty values.
 *
 * @example
 * ```typescript
 * const obj = { a: 1, b: "", c: 3 };
 * const filtered = filterBlankFields(obj); // { a: 1, c: 3 }
 * ```
 */
export const filterBlankFields = (
  obj: Record<string, any>
): Record<string, any> => {
  let filteredObj = {};

  Object.keys(obj).forEach((key) => {
    if (obj[key] && obj[key] !== "") {
      filteredObj[key] = obj[key];
    }
  });

  return filteredObj;
};
/**
 * Validates a file's extension against a list of allowed extensions.
 *
 * @param file - The file object to validate.
 * @param allowedExtensions - An array of allowed file extension strings (e.g., ['jpg', 'png', 'pdf']).
 * @returns Returns true if the file's extension matches one of the allowed extensions; otherwise, returns false.
 *
 * @example
 * validateFile(file, ['jpg', 'png', 'pdf']); // returns true
 * validateFile(file, ['txt', 'doc']);        // returns false
 */
export const validateFile = (
  file: File,
  allowedExtensions: string[]
): boolean => {
  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  return fileExtension ? allowedExtensions.includes(fileExtension) : false;
};

/**
 * Returns the first and last date of the current month in formatted string form.
 *
 * This function calculates the first and last dates of the current month based on the system's current date.
 * The returned dates are formatted using the `dateFormatter2` utility and only the date portion (YYYY-MM-DD) is returned.
 *
 * @returns {Object} An object containing:
 *   - firstDate: {string} The formatted first date of the current month (e.g., "2024-06-01")
 *   - lastDate: {string} The formatted last date of the current month (e.g., "2024-06-30")
 *
 * @example
 * const { firstDate, lastDate } = getCurrentMonthFirstAndLastDate();
 * console.log(firstDate); // "2024-06-01"
 * console.log(lastDate);  // "2024-06-30"
 */
export const getCurrentMonthFirstAndLastDate = () => {
  // Get the current date
  const date = new Date();

  // Calculate the first date of the current month (day 1)
  const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);

  // Calculate the last date of the current month (last day, 23:59:59)
  const lastDate = new Date(
    date.getFullYear(),
    date.getMonth() + 1,
    0,
    23,
    59,
    59
  );

  // Format the dates and return only the date part (YYYY-MM-DD)
  return {
    firstDate: dateFormatter2(firstDate, false).split(" ")[0],
    lastDate: dateFormatter2(lastDate, false).split(" ")[0],
  };
};
/**
 * XML Processing Functions
 *
 * Functions for validating and formatting XML content using fast-xml-parser.
 */

/**
 * Validates and formats an XML string
 *
 * @param xmlString - Raw XML string to validate and format
 * @returns Object containing validation result, error details, and formatted XML
 *
 * @example
 * ```typescript
 * const result = validateAndFormatXML('<root><item>value</item></root>');
 * if (result.isValid) {
 *   console.log(result.formatted); // Pretty-printed XML
 * } else {
 *   console.error(result.error); // Validation error details
 * }
 * ```
 */
export function validateAndFormatXML(xmlString: string) {
  // Validate XML structure and syntax
  const validationResult = XMLValidator.validate(xmlString, {
    allowBooleanAttributes: true,
  });

  // Return error details if validation fails
  if (validationResult !== true) {
    return {
      isValid: false,
      error: validationResult.err,
      formatted: null,
    };
  }

  // Parse XML to JSON object for processing (preserve attributes + declaration)
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
  });
  const jsonObj = parser.parse(xmlString);

  // Rebuild XML with proper formatting
  const builder = new XMLBuilder({
    format: true,
    indentBy: "\t",
    suppressEmptyNode: false,
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
  });
  const formatted = builder.build(jsonObj);

  return {
    isValid: true,
    error: null,
    formatted,
  };
}
/**
 * Storage with Expiry Functions
 *
 * These functions provide localStorage functionality with automatic expiration,
 * useful for caching data that should become invalid after a certain time.
 */

/**
 * Stores an item in localStorage with automatic expiration
 *
 * @param key - Storage key
 * @param value - Value to store
 * @param minutes - Number of minutes until expiration
 * @returns void
 *
 * @example
 * ```typescript
 * setItemWithExpiry("userSession", { userId: 123, token: "abc" }, 30);
 * // Data expires after 30 minutes
 * ```
 */
export function setItemWithExpiry<T>(
  key: string,
  value: T,
  minutes: number
): void {
  const now = new Date();
  const item = {
    expire: now.getTime() + minutes * 60 * 1000, // Expiry in milliseconds
    data: value,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 * Removes an item from localStorage (regardless of expiration)
 *
 * @param key - Storage key to remove
 * @returns void
 *
 * @example
 * ```typescript
 * removeItemWithExpiry("userSession");
 * ```
 */
export function removeItemWithExpiry(key: string): void {
  if (!key || typeof key !== "string") return;
  localStorage.removeItem(key);
}

/**
 * Retrieves an item from localStorage, checking for expiration
 *
 * @param key - Storage key to retrieve
 * @returns Stored value if not expired, null otherwise
 *
 * @example
 * ```typescript
 * const session = getItemWithExpiry("userSession");
 * if (session) {
 *   // Session is still valid
 * } else {
 *   // Session expired or doesn't exist
 * }
 * ```
 */
export function getItemWithExpiry<T>(key: string): T | null {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    if (!item.expire || !item.data) return null;

    // Check if item has expired
    if (new Date().getTime() > item.expire) {
      localStorage.removeItem(key); // Clean up expired entry
      return null;
    }

    return item.data as T;
  } catch {
    return null;
  }
}
/**
 * TPS (Transactions Per Second) Conversion Functions
 *
 * These functions handle conversion between TPS and sleep values
 * for rate limiting and performance control.
 */

/**
 * Converts TPS value to sleep duration
 *
 * @param tps - Transactions per second value
 * @returns Sleep duration value
 *
 * @note Currently returns input value directly, validation logic is commented out
 *
 * @example
 * ```typescript
 * const sleep = convertTpsToSleep(500); // Returns 500
 * ```
 */
export function convertTpsToSleep(tps: number): number {
  // TODO: Implement validation logic
  // tps = tps === 0 ? 0 : tps < 100 ? 100 : tps > 1000 ? 1000 : tps;
  return tps;
}

/**
 * Converts sleep duration to TPS value
 *
 * @param sleep - Sleep duration value
 * @returns Transactions per second value
 *
 * @note Currently returns input value directly, validation logic is commented out
 *
 * @example
 * ```typescript
 * const sleep = convertSleepToTps(100); // Returns 100
 * ```
 */
export function convertSleepToTps(sleep: number): number {
  // TODO: Implement validation logic
  // sleep = sleep === 0 ? 0 : sleep < 10 ? 1000 : sleep > 1000 ? 1000 : sleep;
  return sleep;
}
